import { Button, Checkbox, CircularProgress, FormControlLabel, Grid } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import FilterLine from '../../../components/filter/FilterLine';
import { exportToXlsx } from '../../../excelExportHelpers';
import { useSorting } from '../../../hooks/useSorting';
import { doSwaggerCall, useSwaggerClient } from '../../../hooks/useSwaggerClient';
import PayoutsTable from './components/PayoutsTable';

const BOOKING_PAYOUTS_PAGE_FILTERS = [
    {
        key: 'bookedAtStartDate',
        label: 'Booked on',
        rangeKeys: ['startDate', 'endDate'],
        type: 'daterange'
    },
    {
        key: 'companyId',
        label: 'Vendor name',
        type: 'vendorautocomplete'
    },
    {
        key: 'agentId',
        label: 'Agent name',
        type: 'agentautocomplete'
    },
    {
        key: 'status',
        label: 'Status',
        type: 'statusSelect'
    },
    {
        key: 'feeName',
        label: 'Fee type',
        type: 'text'
    }
];

function getFilterFromUrl(urlSearchPart) {
    const searchParams = new URLSearchParams(urlSearchPart.trim() !== '' ? urlSearchPart : '');
    const startDate =
        searchParams.get('startDate') !== null && moment(searchParams.get('startDate')).isValid()
            ? moment(searchParams.get('startDate'))
            : null;
    const endDate =
        searchParams.get('endDate') !== null && moment(searchParams.get('endDate')).isValid()
            ? moment(searchParams.get('endDate'))
            : null;
    const newCompanyId = parseInt(searchParams.get('companyId'), 10);
    const newAgentId = parseInt(searchParams.get('agentId'), 10);

    return {
        startDate,
        endDate,
        groupActivities: searchParams.get('groupActivities') || false,
        companyId: !Number.isNaN(newCompanyId) ? newCompanyId : '',
        agentId: !Number.isNaN(newAgentId) ? newAgentId : '',
        feeName: searchParams.get('fee') || '',
        status: searchParams.get('status') || 'all'
    };
}

function getUrlFromFilter(filter) {
    const search = new URLSearchParams();
    if (filter.startDate) {
        search.append('startDate', filter.startDate.format('YYYY-MM-DD'));
    }
    if (filter.endDate) {
        search.append('endDate', filter.endDate.format('YYYY-MM-DD'));
    }
    if (filter.companyId) {
        search.append('companyId', filter.companyId);
    }
    if (filter.agentId) {
        search.append('agentId', filter.agentId);
    }
    if (filter.feeName) {
        search.append('fee', filter.feeName);
    }
    if (filter.status) {
        search.append('status', filter.status);
    }
    if (filter.groupActivities) {
        search.append('groupActivities', filter.groupActivities);
    }
    const searchString = search.toString();
    return `?${searchString}`;
}

const PayoutsPage = () => {
    const history = useHistory();
    const location = useLocation();
    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(0);
    const [currentFilters, setFilters] = useState(getFilterFromUrl(location.search));
    const { sort, sortDir, setSorting } = useSorting([
        'booking_item_tickets.booking_items.booking_id',
        'DESC'
    ]);
    const [generatingXlsx, setGeneratingXlsx] = useState(false);
    const [progress, setProgress] = useState(0);

    const setCurrentFilters = useCallback(
        newfilters => history.replace(getUrlFromFilter(newfilters)),
        [history]
    );

    useEffect(() => {
        setFilters(getFilterFromUrl(location.search));
    }, [location.search, setFilters]);

    const [payoutsData, payoutsLoading, payoutsError] = useSwaggerClient(
        'bookings',
        'getBookingPayouts',
        {
            page,
            pageSize,
            startDate: moment(currentFilters.startDate).isValid()
                ? moment(currentFilters.startDate).format('YYYY-MM-DD')
                : '',
            endDate: moment(currentFilters.endDate).isValid()
                ? moment(currentFilters.endDate).format('YYYY-MM-DD')
                : '',
            companyId: currentFilters.companyId,
            agentId: currentFilters.agentId,
            feeName: currentFilters.feeName,
            status: currentFilters.status,
            groupActivities: currentFilters.groupActivities,
            sort,
            sortDir
        }
    );

    const resetFilters = () =>
        setCurrentFilters({
            startDate: null,
            endDate: null,
            groupActivities: false,
            companyId: '',
            agentId: '',
            feeName: '',
            status: ''
        });

    // TODO: can be refactored into excelExportHandler
    // The same pattern is present in bookingpayments/index.js
    const collectAllData = async () => {
        const allPayout = [];
        const localPageSize = 10;
        const totalPayouts = payoutsData?.total;

        for (let i = 0; i < Math.ceil(totalPayouts / localPageSize); i++) {
            const res = await doSwaggerCall('bookings', 'getBookingPayouts', {
                page: i,
                pageSize: localPageSize,
                startDate: moment(currentFilters.startDate).isValid()
                    ? moment(currentFilters.startDate).format('YYYY-MM-DD')
                    : '',
                endDate: moment(currentFilters.endDate).isValid()
                    ? moment(currentFilters.endDate).format('YYYY-MM-DD')
                    : '',
                companyId: currentFilters.companyId,
                agentId: currentFilters.agentId,
                feeName: currentFilters.feeName,
                status: currentFilters.status,
                groupActivities: currentFilters.groupActivities,
                sort,
                sortDir
            });
            res.bookingPayouts.forEach(e => allPayout.push(e));
            setProgress(prevProgress => prevProgress + (localPageSize / totalPayouts) * 100);
        }
        return allPayout;
    };

    useEffect(() => {
        if (payoutsLoading) {
            return;
        }

        if (payoutsData?.bookingPayouts.length === 0 && page !== 0) {
            setPage(0);
        }
    }, [page, payoutsData, payoutsLoading, setPage]);

    return (
        <Grid container className="golf-reservation">
            {payoutsError && (
                <Grid item lg={9} sm={8} xs={12}>
                    <Alert severity="error">
                        <h4>Could not load payouts.</h4>
                    </Alert>
                </Grid>
            )}
            <Grid item xs={12} container justifyContent="space-between">
                <Grid item xs={12} md={8}>
                    <FilterLine
                        possibleFilters={BOOKING_PAYOUTS_PAGE_FILTERS}
                        filters={currentFilters}
                        setFilters={setCurrentFilters}
                        resetFilters={resetFilters}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={4}
                    container
                    justifyContent="flex-end"
                    className="searchfilter-wrapper"
                >
                    <Grid item>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={currentFilters.groupActivities}
                                    onChange={() =>
                                        setFilters({
                                            ...currentFilters,
                                            groupActivities: !currentFilters.groupActivities
                                        })
                                    }
                                />
                            }
                            label="Group by activities"
                        />
                    </Grid>
                    <Grid item>
                        <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={async () => {
                                setGeneratingXlsx(true);

                                let listToExport = [];
                                try {
                                    listToExport = await collectAllData();
                                } catch (err) {
                                    console.log(err);
                                }
                                exportToXlsx(listToExport, 'payouts');
                                setGeneratingXlsx(false);
                                setProgress(0);
                            }}
                        >
                            {generatingXlsx ? (
                                <CircularProgress
                                    variant="determinate"
                                    color="secondary"
                                    size={25}
                                    value={progress}
                                />
                            ) : (
                                'Export to XLSX'
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
            {payoutsLoading && payoutsData?.bookingPayouts.length === 0 ? (
                <Grid container item xs={12} spacing={2} justifyContent="center">
                    <CircularProgress />
                </Grid>
            ) : (
                <PayoutsTable
                    page={page}
                    setPage={setPage}
                    pageSize={pageSize}
                    groupActivities={currentFilters.groupActivities}
                    setPageSize={setPageSize}
                    payoutsData={payoutsData}
                    sort={sort}
                    sortDir={sortDir}
                    setSorting={setSorting}
                />
            )}
        </Grid>
    );
};

export default PayoutsPage;
